import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';
import useFormValidation from 'hooks/useFormValidation';

import SearchableInput from 'components/common/SearchableInput';

import { inventoryApi, shelfApi, productNameApi } from 'api';
import { INVENTORY_TYPE } from 'constants/inventory';

const validationSchema = {
  product: {
    required: 'Product is required',
  },
  inventoryType: {
    required: 'Inventory category is required',
  },
  shelf: {
    required: 'Shelf is required',
  },
  quantity: {
    required: 'Quantity is required',
    type: 'number',
    invalidNumber: 'Please enter a number',
  },
};

const initialValues = {
  product: '',
  productName: '',
  inventoryType: '',
  shelf: '',
  shelfCode: '',
  quantity: '',
};

const CreateInventoryModal = ({ show, onHide, onSuccess }) => {
  const { selectedWarehouse } = useUserContext();

  const [isLoadingInventories, setIsLoadingInventories] = useState(false);
  const [existingInventories, setExistingInventories] = useState({
    count: 0,
    results: [],
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    resetForm,
    isValid,
  } = useFormValidation(initialValues, validationSchema);

  useEffect(() => {
    const fetchExistingInventories = async () => {
      if (!values.product) {
        setExistingInventories({ count: 0, results: [] });
        return;
      }

      const params = {
        product: Number(values.product),
        page_size: 10,
        warehouse: selectedWarehouse?.id,
      };

      if (values.inventoryType) {
        params.inventory_type = values.inventoryType;
      }

      setIsLoadingInventories(true);

      try {
        const response = await inventoryApi.getList(params);
        setExistingInventories(response);
      } catch (error) {
        toast.error('Failed to fetch existing inventories');
      } finally {
        setIsLoadingInventories(false);
      }
    };

    fetchExistingInventories();
  }, [values.product, values.inventoryType, selectedWarehouse?.id]);

  const handleProductInputChange = value => {
    // 입력값이 변경되면 product 초기화
    handleChange('product', '');
    handleChange('productName', value);
  };

  const handleProductSelect = selectedProduct => {
    handleChange('product', selectedProduct.id);
    handleChange('productName', selectedProduct.name);
  };

  const handleShelfInputChange = value => {
    handleChange('shelf', '');
    handleChange('shelfCode', value);
  };

  const handleShelfSelect = selectedShelf => {
    handleChange('shelf', selectedShelf.id);
    handleChange('shelfCode', selectedShelf.code);
  };

  const handleCreateInventory = async () => {
    if (!validateForm()) return;

    try {
      await inventoryApi.create({
        product: Number(values.product),
        quantity: Number(values.quantity),
        inventory_type: values.inventoryType,
        shelf: Number(values.shelf),
        warehouse: selectedWarehouse?.id,
      });

      toast.success('Inventory created successfully');
      closeModal();
      onSuccess();
    } catch (error) {
      toast.error('Failed to create inventory');
    }
  };

  const closeModal = () => {
    resetForm();
    onHide();
  };

  const renderExistingInventories = () => {
    if (!values.product || isLoadingInventories) return null;

    if (existingInventories.count === 0) return null;

    return (
      <div className="mb-3">
        <h6 className="fw-bold mb-1">
          Exist Inventory List (Total: {existingInventories.count})
        </h6>

        {existingInventories.count > 10 && (
          <p className="fs-11 text-danger mb-1">
            Only showing first 10 inventories
          </p>
        )}

        <Table size="sm">
          <thead>
            <tr className="bg-light fs-10">
              <th className="text-start">Shelf Code</th>
              <th className="text-center">Inventory Category</th>
              <th className="text-end">Total Quantity</th>
            </tr>
          </thead>
          <tbody>
            {existingInventories.results.map(inventory => (
              <tr key={inventory.id} className="fs-10">
                <td className="text-start">{inventory.shelf.code}</td>
                <td className="text-center">
                  {inventory.inventory_type.toUpperCase()}
                </td>
                <td className="text-end">{inventory.quantity}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={closeModal}>
      <Modal.Header closeButton>
        <Modal.Title>Create Inventory</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {renderExistingInventories()}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Product<span className="text-danger">*</span>
            </Form.Label>
            <SearchableInput
              apiService={productNameApi}
              searchField="search__icontains"
              value={values.productName}
              onChange={handleProductInputChange}
              onSelect={handleProductSelect}
              onBlur={() => handleBlur('product')}
              placeholder="Enter 2 or more characters to search products..."
              renderItem={item => (
                <div className="font-medium">{item?.name}</div>
              )}
              visibleItems={2}
              disabled={false}
            />
            {touched.product && errors.product && (
              <Form.Text className="text-danger">{errors.product}</Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Inventory Category<span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={values.inventoryType}
              onChange={e => handleChange('inventoryType', e.target.value)}
              onBlur={() => handleBlur('inventoryType')}
              isInvalid={touched.inventoryType && errors.inventoryType}
            >
              <option value="">Select Inventory Category...</option>
              {INVENTORY_TYPE.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.inventoryType}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Shelf<span className="text-danger">*</span>
            </Form.Label>
            <SearchableInput
              apiService={shelfApi}
              searchField="code__icontains"
              additionalParams={{ warehouse: selectedWarehouse?.id }}
              displayField="code"
              value={values.shelfCode}
              onChange={handleShelfInputChange}
              onSelect={handleShelfSelect}
              onBlur={() => handleBlur('shelf')}
              placeholder="Enter 2 or more characters to search shelf..."
              renderItem={item => (
                <div className="font-medium">{item?.code}</div>
              )}
              visibleItems={2}
              disabled={false}
            />
            {touched.shelf && errors.shelf && (
              <Form.Text className="text-danger">{errors.shelf}</Form.Text>
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label>
              Quantity<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="input"
              value={values.quantity}
              onChange={e => handleChange('quantity', e.target.value)}
              placeholder="Enter Quantity..."
              onBlur={() => handleBlur('quantity')}
              isInvalid={touched.quantity && errors.quantity}
            />
            <Form.Control.Feedback type="invalid">
              {errors.quantity}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={resetForm}>
          Clear
        </Button>
        <Button
          variant="primary"
          onClick={handleCreateInventory}
          disabled={!isValid}
        >
          Create
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateInventoryModal;
